import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

const CustomSelect = ({
                          value,
                          onChange,
                          options = [],
                          placeholder = "Select an option",
                          searchable = false,
                          disabled = false,
                          className = "",
                          error = false,
                          dropUp = false,
                          onSearch = null,
                          loading = false
                      }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const searchTimeout = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, searchable]);

    // Filter options based on search term
    // If onSearch is provided, filtering is done server-side; use options as-is
    const filteredOptions = searchable && searchTerm && !onSearch
        ? options.filter(option =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : options;

    // Get selected option label (loose comparison to handle string/number mismatch)
    const selectedOption = options.find(opt => String(opt.value) === String(value));
    const displayValue = selectedOption ? selectedOption.label : placeholder;

    const handleSelect = (optionValue) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Select Button */}
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full px-4 py-2 border rounded-lg flex items-center justify-between transition-colors ${
                    error
                        ? 'border-[#EF4444] focus:border-[#EF4444] focus:ring-1 focus:ring-[#EF4444]'
                        : 'border-[#E5E7EB] focus:border-[#11445B] focus:ring-1 focus:ring-[#11445B]'
                } ${
                    disabled
                        ? 'bg-gray-100 cursor-not-allowed opacity-60'
                        : 'bg-white hover:border-[#11445B]'
                } ${
                    !selectedOption ? 'text-[#6B7280]' : 'text-[#1E293B]'
                }`}
            >
                <span className="truncate">{displayValue}</span>
                <ChevronDown className={`h-4 w-4 text-[#6B7280] transition-transform ${
                    isOpen ? 'transform rotate-180' : ''
                }`} />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className={`absolute z-50 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-60 overflow-hidden ${
                    dropUp ? 'bottom-full mb-1' : 'mt-1'
                }`}>
                    {/* Search Input */}
                    {searchable && (
                        <div className="p-2 border-b border-[#E5E7EB]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSearchTerm(val);
                                        if (onSearch) {
                                            clearTimeout(searchTimeout.current);
                                            searchTimeout.current = setTimeout(() => onSearch(val), 300);
                                        }
                                    }}
                                    placeholder="Search..."
                                    className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#11445B] focus:ring-1 focus:ring-[#11445B]"
                                />
                            </div>
                        </div>
                    )}

                    {/* Options List */}
                    <div className="overflow-y-auto max-h-48">
                        {loading ? (
                            <div className="px-4 py-8 text-center text-[#6B7280] text-sm">
                                Searching...
                            </div>
                        ) : filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleSelect(option.value)}
                                    className={`w-full px-4 py-2.5 text-left hover:bg-[#F4F4F4] transition-colors flex items-center justify-between ${
                                        String(value) === String(option.value) ? 'bg-[#F0F9FF] text-[#11445B]' : 'text-[#1E293B]'
                                    }`}
                                >
                                    <span>{option.label}</span>
                                    {String(value) === String(option.value) && (
                                        <Check className="h-4 w-4 text-[#11445B]" />
                                    )}
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-8 text-center text-[#6B7280] text-sm">
                                No options found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomSelect;
